import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  ReadOutlined,
  ScheduleOutlined,
  UserOutlined,
  CarryOutOutlined,
  SmileOutlined,
  MenuOutlined,
  FileOutlined,
  BookOutlined,
  PushpinOutlined,
  HeartOutlined,
  TrophyOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { getDataDoc } from "../database";
import { testSlice } from "../redux/store";
import { getProfessorAccessibleClasses } from "../utils";
import "./MobileNavigation.css";

const { actions } = testSlice;
const { GET_USER } = actions;

const MobileBottomNavigation = ({ userType, onMenuOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const clase = useSelector((state) => state.clase);
  const profesori = useSelector((state) => state.profesori);
  const [showClasses, setShowClasses] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [userClasses, setUserClasses] = useState([]);
  const [currentClass, setCurrentClass] = useState(null);

  // Fetch classes based on user type
  const fetchUserClasses = async () => {
    if (userType === "admin") {
      // For admin, get all classes with diriginte info
      const claseWithDiriginte = await Promise.all(
        clase.map(async (cls) => {
          const clasData = await getDataDoc("claseData", cls.id);
          return { ...cls, clasData };
        })
      );
      setUserClasses(claseWithDiriginte);
    } else if (userType === "profesor") {
      // Folosește noua funcție din utils
      const accessibleClases = await getProfessorAccessibleClasses(
        user.uid,
        clase
      );

      // Adaugă clasData pentru fiecare clasă
      const claseWithData = await Promise.all(
        accessibleClases.map(async (cls) => {
          const clasData = await getDataDoc("claseData", cls.id);
          return { ...cls, clasData };
        })
      );

      setUserClasses(claseWithData);
    }
  };

  // Get diriginte name
  const getDiriginteName = (clasa) => {
    if (!clasa.clasData?.diriginte || !profesori) return "Fără diriginte";

    const diriginteProf = profesori?.find(
      (p) => p.id === clasa.clasData.diriginte
    );
    if (!diriginteProf) return "Fără diriginte";

    return `${diriginteProf.numeDeFamilie} ${diriginteProf.prenume}`;
  };

  // Check if currently in a class page and get class info
  const getCurrentClass = async () => {
    const match = location.pathname.match(/^\/class\/(.+)$/);
    if (match) {
      const classId = match[1];
      const clasa = clase?.find((c) => c.id === classId);
      if (clasa) {
        const clasData = await getDataDoc("claseData", clasa.id);
        setCurrentClass({ ...clasa, clasData });
        return;
      }
    }
    setCurrentClass(null);
  };

  useEffect(() => {
    if (
      clase?.length > 0 &&
      (userType === "admin" || userType === "profesor")
    ) {
      fetchUserClasses();
    }
  }, [clase, user, userType]);

  useEffect(() => {
    if (clase?.length > 0) {
      getCurrentClass();
    }
  }, [location.pathname, clase]);

  // Define main navigation items (always visible)
  const getMainNavItems = () => {
    // Check if we're on an elev or profesor page
    const isElevPage = location.pathname.includes("/elev/");
    const isProfesorPage = location.pathname.includes("/profesor/");

    if (userType === "admin" || userType === "profesor") {
      // Determine label for classes button
      const classesLabel = currentClass
        ? `${currentClass.anClasa} ${currentClass.identificator}`
        : "Clase";

      const items = [];

      // Add back button if on elev or profesor page
      if (isElevPage || isProfesorPage) {
        items?.push({
          key: "back",
          icon: <ArrowLeftOutlined />,
          label: "Înapoi",
          special: true,
          action: () => navigate(-1),
        });
      }

      // Add parent mode switch if user has hybrid mode (but not on elev/profesor page to save space) - FIRST
      if (user.mode === "hybrid" && !isElevPage && !isProfesorPage) {
        items?.push({
          key: "parent-mode",
          icon: <UserOutlined />,
          label: "Mod Părinte",
          special: true,
        });
      }

      items?.push({
        key: "clase",
        icon: <ReadOutlined />,
        label: classesLabel,
        special: true,
      });

      items?.push({
        key: "more",
        icon: <MenuOutlined />,
        label: "Mai multe",
        special: true,
      });

      return items;
    } else if (userType === "elevi") {
      const items = [
        { key: "elev-catalog", icon: <CarryOutOutlined />, label: "Note" },
        { key: "orar", icon: <ScheduleOutlined />, label: "Orar" },
        { key: "teme", icon: <FileOutlined />, label: "Teme" },
        { key: "lectii-elev", icon: <BookOutlined />, label: "Lecții" },
      ];

      // Add profesor mode switch if user has hybrid mode
      if (user.mode === "hybrid") {
        items?.push({
          key: "profesor-mode",
          icon: <UserOutlined />,
          label: "Mod Profesor",
          special: true,
        });
      }

      items?.push({ key: "profil", icon: <SmileOutlined />, label: "Profil" });

      return items;
    }
    return [];
  };

  // Define "more" options for professors/admins
  const getMoreOptions = () => {
    if (userType === "admin") {
      return [
        { key: "orar", icon: <ScheduleOutlined />, label: "Orar" },
        { key: "profesori", icon: <UserOutlined />, label: "Profesori" },
        { key: "anunturi", icon: <CarryOutOutlined />, label: "Anunțuri" },
        { key: "condica", icon: <BookOutlined />, label: "Condica" },
        { key: "feedback", icon: <TrophyOutlined />, label: "Feedback" },
        { key: "profil", icon: <SmileOutlined />, label: user.displayName },
      ];
    } else if (userType === "profesor") {
      return [
        { key: "orar", icon: <ScheduleOutlined />, label: "Orar" },
        { key: "condica", icon: <BookOutlined />, label: "Condica" },
        { key: "feedback", icon: <TrophyOutlined />, label: "Feedback" },
        { key: "profil", icon: <SmileOutlined />, label: "Profil" },
      ];
    }
    return [];
  };

  const handleItemClick = (item) => {
    if (item.special) {
      if (item.key === "clase") {
        setShowClasses(true);
      } else if (item.key === "more") {
        setShowMoreOptions(true);
      } else if (item.key === "parent-mode") {
        handleParentModeSwitch();
      } else if (item.key === "profesor-mode") {
        handleProfesorModeSwitch();
      } else if (item.key === "back" && item.action) {
        item.action();
      }
    } else {
      navigate("/" + item.key);
    }
  };

  const handleParentModeSwitch = async () => {
    try {
      let u = await getDataDoc("users", user.id || user.uid);
      let e = u?.copii[0].idElev;

      let data = await getDataDoc("elevi", e);
      if (!data) return;

      let dataUser = { ...user, ...data };
      data = await getDataDoc("claseData", dataUser?.clasa);

      dispatch(
        GET_USER({
          ...dataUser,
          clasaMea: data,
          type: "elevi",
          mainType: "parinte",
          altiCopii: u?.copii,
          uid: e,
          id: e,
          oldUid: user.id || user.uid,
        })
      );
      navigate("/");
    } catch (error) {
      console.error("Error switching to parent mode:", error);
    }
  };

  const handleProfesorModeSwitch = async () => {
    try {
      let u = await getDataDoc("users", user.id || user.uid);
      dispatch(
        GET_USER({
          ...user,
          ...u,
          type: "profesor",
          mainType: "profesor",
          copii: [],
          id: user.oldUid,
          uid: user.oldUid,
          // Suprascrie datele cu cele ale profesorului pentru a fi folosite când adaugă note
          displayName: u.displayName,
          adresaEmail: u.adresaEmail,
          numeDeFamilie: u.numeDeFamilie,
          prenume: u.prenume,
          photoLink: u.photoLink,
        })
      );
      navigate("/");
    } catch (error) {
      console.error("Error switching to profesor mode:", error);
    }
  };

  const handleClassClick = (clasa) => {
    navigate(`/class/${clasa.id}`);
    setShowClasses(false);
  };

  const handleMoreOptionClick = (option) => {
    navigate("/" + option.key);
    setShowMoreOptions(false);
  };

  const mainNavItems = getMainNavItems();
  const moreOptions = getMoreOptions();

  // Show more options view
  if (showMoreOptions) {
    return (
      <div className="mobile-bottom-nav more-options-view">
        <div className="classes-header">
          <div
            className="mobile-nav-item back-button close-button"
            onClick={() => setShowMoreOptions(false)}
          >
            <div className="mobile-nav-icon">
              <CloseOutlined />
            </div>
          </div>
          <div className="classes-title">
            <span>Mai multe opțiuni</span>
          </div>
          <div className="mobile-nav-item" style={{ visibility: "hidden" }}>
            <div className="mobile-nav-icon"></div>
            <span className="mobile-nav-label"></span>
          </div>
        </div>
        <div className="classes-list">
          {moreOptions.map((option) => (
            <div
              key={option.key}
              className="class-item"
              onClick={() => handleMoreOptionClick(option)}
            >
              <div className="class-icon">{option.icon}</div>
              <div className="class-info">
                <div className="class-name">{option.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Show classes view
  if (showClasses) {
    return (
      <div className="mobile-bottom-nav classes-view">
        <div className="classes-header">
          <div
            className="mobile-nav-item back-button close-button"
            onClick={() => setShowClasses(false)}
          >
            <div className="mobile-nav-icon">
              <CloseOutlined />
            </div>
          </div>
          <div className="classes-title">
            <span>Selectează clasa</span>
          </div>
          <div className="mobile-nav-item" style={{ visibility: "hidden" }}>
            <div className="mobile-nav-icon"></div>
            <span className="mobile-nav-label"></span>
          </div>
        </div>
        <div className="classes-list">
          {userClasses.map((clasa) => (
            <div
              key={clasa.id}
              className="class-item"
              onClick={() => handleClassClick(clasa)}
            >
              <div className="class-icon">
                <ReadOutlined />
              </div>
              <div className="class-info">
                <div className="class-name">
                  {clasa.anClasa} {clasa.identificator}
                </div>
                <div className="class-details">{getDiriginteName(clasa)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Main navigation
  return (
    <div className="mobile-bottom-nav">
      {mainNavItems?.map((item) => (
        <div
          key={item.key}
          className="mobile-nav-item"
          onClick={() => handleItemClick(item)}
        >
          <div className="mobile-nav-icon">{item.icon}</div>
          <span className="mobile-nav-label">{item.label}</span>
        </div>
      ))}
    </div>
  );
};

export default MobileBottomNavigation;
